import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';

interface HowItWorksProps {
  language: 'pt' | 'en';
  onOpenForm: () => void;
}

export default function HowItWorks({ language, onOpenForm }: HowItWorksProps) {
  const [sectionRef, isVisible] = useIntersectionObserver<HTMLElement>({ threshold: 0.1 });
  const content = {
    pt: {
      eyebrow: 'Como funciona',
      title: 'Três etapas.',
      subtitle: 'O processo é direto, técnico e respeita seu tempo.',
      steps: [
        {
          number: '01',
          title: 'Diagnóstico',
          description: 'Compartilha informações essenciais sobre seu objetivo, capacidade financeira e horizonte de planejamento. Leva menos de 3 minutos.'
        },
        {
          number: '02',
          title: 'Análise preliminar pela FGT Capital',
          description: 'Nossa equipe avalia os dados enviados, mapeia cenários disponíveis e prepara uma leitura estrutural do seu caso.'
        },
        {
          number: '03',
          title: 'Reunião estratégica',
          description: 'Apresentamos o diagnóstico em uma conversa de 30 a 45 minutos. Você decide os próximos passos. Sem pressão comercial.'
        }
      ],
      cta: 'Agendar Diagnóstico'
    },
    en: {
      eyebrow: 'How it works',
      title: 'Three steps.',
      subtitle: 'The process is direct, technical and respects your time.',
      steps: [
        {
          number: '01',
          title: 'Diagnosis',
          description: 'Share essential information about your objective, financial capacity and planning horizon. Takes less than 3 minutes.'
        },
        {
          number: '02',
          title: 'Preliminary analysis by FGT Capital',
          description: 'Our team evaluates the submitted data, maps available scenarios and prepares a structural reading of your case.'
        },
        {
          number: '03',
          title: 'Strategic meeting',
          description: 'We present the assessment in a 30 to 45-minute conversation. You decide the next steps. No sales pressure.'
        }
      ],
      cta: 'Schedule Strategic Assessment'
    }
  };

  const t = content[language];

  return (
    <section id="how-it-works" ref={sectionRef} className="relative bg-white border-b border-[#E7E1D9] overflow-hidden">
      {/* Decorative image — right half, grayscale, low opacity */}
      <div className="absolute top-0 right-0 w-1/2 h-full pointer-events-none select-none overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/5bf041fc4c5de993b35c7e675ad5c6ef.jpg"
          alt=""
          className="w-full h-full object-cover object-left-top"
          style={{
            filter: 'grayscale(100%)',
            opacity: isVisible ? 0.12 : 0,
            transform: isVisible ? 'scale(1)' : 'scale(1.06)',
            transition: 'opacity 1.2s ease-out, transform 1.4s ease-out',
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <div className="max-w-2xl mb-16">
          <h2 className="text-sm uppercase tracking-widest text-[#2E2E2E] mb-6 font-semibold">
            {t.eyebrow}
          </h2>
          <p className="text-4xl lg:text-5xl font-semibold text-[#0B0B0B] leading-tight mb-4">
            {t.title}
          </p>
          <p className="text-lg text-[#2E2E2E]">
            {t.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {t.steps.map((step, index) => (
            <div key={index} className="relative">
              <div className="mb-6">
                <span className="inline-flex items-center justify-center w-12 h-12 border-2 border-[#F97700] text-[#F97700] font-mono text-sm font-semibold">
                  {step.number}
                </span>
              </div>
              <h3 className="text-xl font-semibold text-[#0B0B0B] mb-3">
                {step.title}
              </h3>
              <p className="text-base text-[#2E2E2E] leading-relaxed">
                {step.description}
              </p>
            </div>
          ))}
        </div>

        <div className="flex justify-center pt-8">
          <button
            onClick={onOpenForm}
            className="inline-flex items-center justify-center px-8 py-4 bg-[#F97700] text-white text-base font-semibold tracking-wide hover:bg-[#E56E00] transition-colors whitespace-nowrap cursor-pointer"
          >
            {t.cta}
          </button>
        </div>
      </div>
    </section>
  );
}